import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Search, ShieldCheck, Flame, Plus, X, Loader2, Award, ChevronLeft } from 'lucide-react';
import { useAuth } from '../App';
import { NudgePanel } from './skills/Feedback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/skills`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const ROLE = { millwright: 'Millwright', millwright_foreman: 'Foreman', welder: 'Welder', labourer: 'Labourer', apprentice: 'Apprentice', fitter: 'Fitter' };

// ★★★☆☆ — click to rate (office admins), read-only for everyone else. Clicking the current value clears it.
export const Stars = ({ value = 0, onChange, size = 'w-5 h-5', testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid} aria-label={`${value} of 5 stars`}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n === value ? 0 : n)}
        className={`${onChange ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`} data-testid={testid ? `${testid}-${n}` : undefined} title={onChange ? `${n} star${n > 1 ? 's' : ''}` : undefined}>
        <Star className={`${size} ${n <= value ? 'fill-amber-400 text-amber-500' : 'text-slate-300'}`} />
      </button>
    ))}
  </span>
);

function WeldingTickets({ userId, tickets, safetyTickets, canRate, processes, positions, onChanged }) {
  const [form, setForm] = useState({ process: 'SMAW', positions: [], material: 'Carbon steel', expiry: '', ticket_id: '' });
  const [busy, setBusy] = useState(false);
  const toggle = (p) => setForm(f => ({ ...f, positions: f.positions.includes(p) ? f.positions.filter(x => x !== p) : [...f.positions, p] }));
  const add = async () => {
    setBusy(true);
    try { await axios.post(`${API}/profiles/${userId}/welding-tickets`, form, auth()); toast.success(`${form.process} ${form.positions.join('/')} ticket added`); setForm(f => ({ ...f, positions: [], expiry: '' })); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not add ticket'); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await axios.delete(`${API}/profiles/${userId}/welding-tickets/${id}`, auth()); onChanged(); } catch (e) { toast.error('Could not remove'); } };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="welding-tickets">
      <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5"><Flame className="w-4 h-4 text-orange-500" /> Welding tickets (CWB-style)</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {(tickets || []).length === 0 && <span className="text-xs text-slate-500">None recorded.</span>}
        {(tickets || []).map(t => {
          const expired = t.expiry && t.expiry < today;
          return (
            <span key={t.id} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${expired ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-orange-50 border-orange-200 text-orange-900'}`} data-testid={`weld-ticket-${t.id}`}>
              {t.process} · {t.positions.join('/')} · {t.material}{t.expiry ? ` · ${expired ? 'EXPIRED ' : 'exp '}${t.expiry}` : ''}
              {canRate && <button onClick={() => remove(t.id)} className="rounded p-0.5 hover:bg-white/60" title="Remove" data-testid={`weld-ticket-remove-${t.id}`}><X className="w-3 h-3" /></button>}
            </span>
          );
        })}
      </div>
      {canRate && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto_auto] gap-2 items-end">
          <label className="text-[11px] text-slate-600">Process
            <select value={form.process} onChange={e => setForm(f => ({ ...f, process: e.target.value }))} className="block mt-0.5 h-8 px-2 rounded-lg border border-slate-300 text-xs" data-testid="weld-process">
              {processes.map(p => <option key={p}>{p}</option>)}
            </select>
          </label>
          <div className="text-[11px] text-slate-600">Positions
            <div className="mt-0.5 flex flex-wrap gap-1">
              {positions.map(p => (
                <button key={p} type="button" onClick={() => toggle(p)} className={`h-8 px-2.5 rounded-lg border text-xs font-bold ${form.positions.includes(p) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-700'}`} data-testid={`weld-pos-${p}`}>{p}</button>
              ))}
            </div>
          </div>
          <label className="text-[11px] text-slate-600">Material
            <input value={form.material} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} className="block mt-0.5 h-8 w-32 px-2 rounded-lg border border-slate-300 text-xs" data-testid="weld-material" />
          </label>
          <label className="text-[11px] text-slate-600">Expiry
            <input type="date" value={form.expiry} onChange={e => setForm(f => ({ ...f, expiry: e.target.value }))} className="block mt-0.5 h-8 px-2 rounded-lg border border-slate-300 text-xs" data-testid="weld-expiry" />
          </label>
          <button onClick={add} disabled={busy || !form.positions.length} className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40" data-testid="weld-add">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add ticket
          </button>
          {safetyTickets?.length > 0 && (
            <label className="text-[11px] text-slate-600 md:col-span-5">Link to an uploaded ticket (optional)
              <select value={form.ticket_id} onChange={e => setForm(f => ({ ...f, ticket_id: e.target.value }))} className="block mt-0.5 h-8 px-2 rounded-lg border border-slate-300 text-xs w-full md:w-auto" data-testid="weld-link">
                <option value="">— none —</option>
                {safetyTickets.map(t => <option key={t.id} value={t.id}>{t.name}{t.expiry ? ` (exp ${t.expiry})` : ''}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileEditor({ userId, catalog, onBack, onSummary, meId }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const load = () => axios.get(`${API}/profiles/${userId}`, auth()).then(r => { setData(r.data); setNotes(r.data.profile.notes || ''); onSummary?.(userId, r.data.summary); }).catch(e => toast.error(e.response?.data?.detail || 'Could not load profile'));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line
  if (!data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const { person, profile, tickets, ticket_backed, summary, can_rate } = data;
  const rate = async (key, stars) => {
    setData(d => ({ ...d, profile: { ...d.profile, ratings: { ...d.profile.ratings, [key]: stars ? { stars } : undefined } } }));
    try { const r = await axios.put(`${API}/profiles/${userId}/ratings`, { ratings: { [key]: stars } }, auth()); setData(d => ({ ...d, profile: r.data.profile, summary: r.data.summary })); onSummary?.(userId, r.data.summary); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); load(); }
  };
  const saveNotes = async () => { try { await axios.put(`${API}/profiles/${userId}/ratings`, { ratings: {}, notes }, auth()); toast.success('Notes saved'); } catch (e) { toast.error('Could not save notes'); } };
  return (
    <div className="space-y-4" data-testid="skill-profile">
      <div className="flex flex-wrap items-center gap-3">
        {onBack && <button onClick={onBack} className="h-9 px-2 rounded-lg border border-slate-300 text-xs font-semibold inline-flex items-center gap-1 lg:hidden" data-testid="profile-back"><ChevronLeft className="w-4 h-4" /> Roster</button>}
        <div>
          <h2 className="text-xl font-black text-slate-900">{person.full_name}</h2>
          <p className="text-xs text-slate-500">{ROLE[person.role] || person.role} · {summary.rated} of {summary.of} skills rated{summary.avg ? ` · ${summary.avg}★ average` : ''}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {summary.top.map(t => <span key={t.key} className="rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-semibold px-2 py-0.5" data-testid={`top-skill-${t.key}`}>{t.stars}★ {t.name}</span>)}
        </div>
      </div>
      {can_rate && <NudgePanel userId={userId} nudges={data.nudges} onChanged={(ns) => { setData(d => ({ ...d, nudges: ns })); load(); }} />}
      {!can_rate && <p className="text-[11px] text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">Ratings are set by the office. {person.id === meId ? 'This is how MASON sees your skills when it builds a crew.' : ''}</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {catalog.groups.map(g => (
          <div key={g.group} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`skill-group-${g.group.replace(/\W+/g, '-').toLowerCase()}`}>
            <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">{g.group}</p>
            {g.skills.filter(s => !s.archived).map(s => {
              const r = profile.ratings?.[s.key];
              return (
                <div key={s.key} className="flex items-center justify-between gap-3 py-1 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-800 inline-flex items-center gap-1.5">{s.name}
                    {ticket_backed[s.key] && <span title={`Live ticket: ${ticket_backed[s.key]}`} data-testid={`ticket-badge-${s.key}`}><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /></span>}
                  </span>
                  <span className="inline-flex items-center gap-2 shrink-0">
                    <Stars value={r?.stars || 0} onChange={can_rate ? (v) => rate(s.key, v) : undefined} testid={`stars-${s.key}`} />
                    <span className="text-[10px] text-slate-400 w-16 text-right hidden sm:inline">{r?.stars ? catalog.stars[r.stars] : ''}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <WeldingTickets userId={userId} tickets={profile.welding_tickets} safetyTickets={tickets} canRate={can_rate} processes={catalog.weld_processes} positions={catalog.weld_positions} onChanged={load} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="safety-tickets">
          <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-emerald-600" /> Safety tickets on file</p>
          {tickets.length === 0 ? <p className="text-xs text-slate-500 mt-1">None found for this person.</p> : (
            <ul className="mt-1.5 space-y-0.5">{tickets.map(t => <li key={t.id} className="text-xs text-slate-700 flex justify-between gap-2"><span>{t.name}</span><span className={`${t.status === 'expired' ? 'text-rose-600' : 'text-slate-400'}`}>{t.expiry || t.status}</span></li>)}</ul>
          )}
        </div>
        {data.feedback?.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 md:col-span-2" data-testid="feedback-history">
            <p className="text-sm font-bold text-slate-900">How PMs rated recent jobs</p>
            <ul className="mt-1.5 space-y-1">
              {data.feedback.map((f, i) => (
                <li key={i} className="text-xs text-slate-700 flex flex-wrap gap-x-2 gap-y-0.5">
                  <span className="text-slate-500">{f.job_date}</span><span className="font-semibold">{f.wo}{f.customer ? ` ${f.customer}` : ''}</span>
                  {f.overall ? <span className="text-amber-700">{f.overall}★ overall</span> : null}
                  <span className="text-slate-500">{Object.entries(f.skills || {}).map(([k, v]) => `${v}★ ${catalog.groups.flatMap(g => g.skills).find(s => s.key === k)?.name || k}`).join(' · ')}</span>
                  <span className="text-slate-400">— {f.by_name}{f.note ? `: “${f.note}”` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-bold text-slate-900">Dispatcher notes</p>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} readOnly={!can_rate} rows={4} placeholder={can_rate ? 'e.g. great on precision work, prefers not to travel, strong with Fike' : ''} className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900" data-testid="profile-notes" />
          {can_rate && <button onClick={saveNotes} className="mt-2 h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold" data-testid="profile-notes-save">Save notes</button>}
        </div>
      </div>
    </div>
  );
}

export default function SkillProfiles() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/catalog`, auth()).then(r => setCatalog(r.data)).catch(() => toast.error('Could not load skill catalog'));
    axios.get(`${API}/profiles`, auth()).then(r => { setPeople(r.data.people); if (r.data.people.length === 1) setSel(r.data.people[0].id); }).catch(() => setPeople([]));
  }, []);
  const list = useMemo(() => (people || []).filter(p => !q || p.full_name.toLowerCase().includes(q.toLowerCase())), [people, q]);
  const onSummary = (id, summary) => setPeople(ps => ps.map(p => (p.id === id ? { ...p, summary } : p)));
  if (!catalog || !people) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const soloView = people.length === 1 && people[0].id === user?.id;
  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto" data-testid="skill-profiles-page">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Skill Profiles</h1>
        <p className="text-sm text-slate-600">{soloView ? 'Your capability profile — what MASON uses when it builds a crew.' : 'Stars per skill for every field guy, welding tickets and safety tickets. MASON uses this plus the schedule to pick the best crew for a dispatch request.'}</p>
      </div>
      <div className={`grid grid-cols-1 ${soloView ? '' : 'lg:grid-cols-[320px_1fr]'} gap-4`}>
        {!soloView && (
          <aside className={`${mobileOpen || !sel ? '' : 'hidden'} lg:block rounded-xl border border-slate-200 bg-white p-3 self-start lg:sticky lg:top-4`} data-testid="skill-roster">
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a guy" className="w-full h-9 pl-8 pr-2 rounded-lg border border-slate-300 text-sm" data-testid="roster-search" />
            </div>
            <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-auto">
              {list.map(p => (
                <li key={p.id}>
                  <button onClick={() => { setSel(p.id); setMobileOpen(false); }} className={`w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 ${sel === p.id ? 'bg-slate-100' : ''}`} data-testid={`roster-${p.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-900">{p.full_name}{p.nudges > 0 && <span className="ml-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5" title="MASON has star suggestions" data-testid={`roster-nudges-${p.id}`}>{p.nudges}</span>}</span>
                      <span className="text-[11px] text-slate-500">{ROLE[p.role] || p.role}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-500">{p.summary.rated ? `${p.summary.rated} skills · ${p.summary.avg}★ avg` : 'not rated yet'}{p.summary.welding_tickets ? ` · ${p.summary.welding_tickets} weld ticket${p.summary.welding_tickets > 1 ? 's' : ''}` : ''}</span>
                      {p.summary.top[0] && <span className="text-[10px] text-amber-700 truncate max-w-[120px]">{p.summary.top[0].stars}★ {p.summary.top[0].name}</span>}
                    </div>
                  </button>
                </li>
              ))}
              {list.length === 0 && <li className="text-xs text-slate-500 px-2 py-3">Nobody matches.</li>}
            </ul>
          </aside>
        )}
        <main className={`${!sel && !soloView ? 'hidden lg:block' : ''}`}>
          {sel ? <ProfileEditor userId={sel} catalog={catalog} meId={user?.id} onBack={soloView ? null : () => { setMobileOpen(true); setSel(null); }} onSummary={onSummary} />
            : <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="profile-empty">Pick someone from the roster to see or rate their skills.</div>}
        </main>
      </div>
    </div>
  );
}
